import { useEffect, useRef, useState, type FormEvent } from "react";
import s from "./Projects.module.css";
import { useDecideGate } from "../../api/queries";
import {
  decisionRequiresNote,
  GATE_DECISION_VERB,
  GATE_DECISIONS,
  type Gate,
  type GateDecision,
  type GateDecisionResult,
} from "../../domain/projects";
import { Button, Field, Input, Select } from "../../design/components";
import { t } from "../../i18n";

/**
 * Decide a stage gate: approve, reject or waive.
 *
 * Two things this dialog does deliberately.
 *
 * **It shows what is missing, and lets the decision happen anyway.** The
 * platform does not refuse a gate approved with its evidence unmet, because
 * refusing produces gates decided in a meeting and recorded nowhere. It
 * records the gap instead — on the gate, on the event and in the audit log,
 * visible for the life of the project. So the missing items are put in front
 * of the person deciding, in the sentence the platform will file against
 * their name, and the button stays enabled.
 *
 * **A waiver requires its reason before the button enables.** That one the
 * platform does refuse, on the grounds that a waiver with no reason reads
 * afterwards as a gate that never applied. Requiring it here prevents the
 * refusal rather than displaying it; the platform still enforces it.
 */
export function GateDecisionDialog({
  projectId,
  gate,
  onClose,
  onDecided,
}: {
  projectId: string;
  gate: Gate;
  onClose: () => void;
  onDecided: (result: GateDecisionResult) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const decide = useDecideGate(projectId);
  const [decision, setDecision] = useState<GateDecision>("approved");
  const [note, setNote] = useState("");
  const [supplying, setSupplying] = useState<string[]>([]);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);

  const noteRequired = decisionRequiresNote(decision);
  const blocked = noteRequired && !note.trim();
  /* What would still be missing after this decision, given anything ticked here. */
  const missing = gate.evidence.missing.filter((k) => !supplying.includes(k));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (blocked) return;
    decide.mutate(
      { gateKey: gate.key, decision, note: note.trim() || undefined, evidence: supplying },
      { onSuccess: onDecided },
    );
  };

  return (
    <dialog ref={ref} onClose={onClose} style={dialogStyle} aria-label={t("Decide {gate}", { gate: gate.name })}>
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15 }}>{t("Decide gate {n} — {gate}", { n: gate.seq, gate: gate.name })}</h2>
          <div className={s.dialogNote} style={{ marginTop: 4 }}>
            {t("Deciding a gate needs")} <span className="mono">project:gate</span>
            {t(
              ", which is not the permission that updates a project. The decision, its note and anything still missing are written to the audit log.",
            )}
          </div>
        </div>

        <Field label="Decision">
          <Select
            value={decision}
            onChange={(e) => setDecision(e.target.value as GateDecision)}
            aria-label={t("Decision")}
          >
            {GATE_DECISIONS.map((dcn) => (
              <option key={dcn} value={dcn}>
                {t(GATE_DECISION_VERB[dcn])}
              </option>
            ))}
          </Select>
        </Field>

        {gate.evidence.required.length > 0 && (
          <div>
            <div className={s.moneyLabel}>{t("Evidence this gate asks for")}</div>
            <div className={s.chips} data-testid="gate-evidence">
              {gate.evidence.required.map((k) => {
                const supplied = gate.evidence.supplied.includes(k);
                const ticking = supplying.includes(k);
                return (
                  <label
                    key={k}
                    className={`${s.chip} ${supplied || ticking ? s.chipSupplied : s.chipMissing}`}
                    style={{ cursor: supplied ? "default" : "pointer" }}
                  >
                    {!supplied && (
                      <input
                        type="checkbox"
                        checked={ticking}
                        onChange={(e) =>
                          setSupplying(e.target.checked ? [...supplying, k] : supplying.filter((x) => x !== k))
                        }
                        aria-label={t("Supplied: {item}", { item: k })}
                        style={{ marginInlineEnd: 5 }}
                      />
                    )}
                    {k}
                  </label>
                );
              })}
            </div>
            {missing.length > 0 && (
              <div className={s.warn} role="status" data-testid="deciding-without-evidence">
                {t(
                  "{n} of {total} required item(s) will not have been supplied: {items}. This gate can still be decided — the platform records that it was, and the record stays on the gate for the life of the project.",
                  { n: missing.length, total: gate.evidence.required.length, items: missing.join(", ") },
                )}
              </div>
            )}
          </div>
        )}

        <Field
          label={
            noteRequired
              ? "Reason for the waiver (required) — a waiver with no reason reads afterwards as a gate that never applied"
              : "Note"
          }
        >
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required={noteRequired}
            aria-label={noteRequired ? t("Reason for the waiver") : t("Note")}
            placeholder={noteRequired ? t("e.g. Issued against the standing civils framework") : t("Optional")}
          />
        </Field>

        {decide.isError && (
          <div className={s.bad} role="alert">
            {t("Refused: {message}", { message: (decide.error as Error).message })}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={decide.isPending}>
            {t("Cancel")}
          </Button>
          <Button
            type="submit"
            variant={decision === "approved" ? "primary" : "danger"}
            disabled={decide.isPending || blocked}
          >
            {decide.isPending ? t("Recording…") : t("{verb} gate", { verb: t(GATE_DECISION_VERB[decision]) })}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

const dialogStyle: React.CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(620px, 92vw)",
};
